// ==========================================================================
// components/ShareCharts.tsx — the charts on Money → Shares (FF-048, 10 Oct 2026)
//   James: "Can we get some colour and widgets/graphs … to give it some flare."
//   Plain SVG and divs, no chart library. Colours: chart-5 (capital gains) and
//   chart-2 (distributions) — the pair passes the colour-blind and contrast
//   checks; primary for single-series charts. Every mark has a hover tooltip.
// ==========================================================================
import { useState } from "react";
import { format, parseISO } from "date-fns";
import { money } from "@/lib/money";
import { tokenColour } from "@/lib/calendar";

const GAIN = () => tokenColour("chart-5"); const INCOME = () => tokenColour("chart-2");

function Legend({ items }: { items: { label: string; colour: string }[] }) {
  return <div className="flex flex-wrap gap-4 text-xs text-ink-muted">{items.map((i) => <span key={i.label} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.colour }} />{i.label}</span>)}</div>;
}

/** Capital gains and distributions per financial year, side by side. Losses go below the line. */
export function YearBars({ rows, selected, onPick }: { rows: { fy: string; gain: number; income: number }[]; selected: string; onPick: (fy: string) => void }) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(1, ...rows.map((r) => Math.max(r.gain, r.income)));
  const min = Math.min(0, ...rows.map((r) => r.gain));
  const H = 150; const span = max - min; const zero = (max / span) * H;
  const h = (v: number) => (Math.abs(v) / span) * H;
  const tip = rows.find((r) => r.fy === hover);
  return (
    <div className="space-y-3">
      <Legend items={[{ label: "Net capital gain", colour: GAIN() }, { label: "Distributions and dividends", colour: INCOME() }]} />
      <div className="relative">
        <div className="relative flex gap-3" style={{ height: H }}>
          <div className="pointer-events-none absolute inset-x-0 border-t border-line" style={{ top: zero }} />
          {rows.map((r) => (
            <button key={r.fy} type="button" onClick={() => onPick(r.fy)} onMouseEnter={() => setHover(r.fy)} onMouseLeave={() => setHover(null)}
              className={`relative flex-1 rounded-md transition ${r.fy === selected ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-surface-raised"}`} aria-label={`FY ${r.fy}`}>
              <span className="absolute inset-x-0 flex justify-center gap-0.5 px-1" style={{ top: 0, height: zero, alignItems: "flex-end" }}>
                <span className="w-1/3 max-w-6 rounded-t" style={{ height: r.gain > 0 ? Math.max(2, h(r.gain)) : 0, background: GAIN() }} />
                <span className="w-1/3 max-w-6 rounded-t" style={{ height: r.income > 0 ? Math.max(2, h(r.income)) : 0, background: INCOME() }} />
              </span>
              {r.gain < 0 && <span className="absolute inset-x-0 flex justify-center gap-0.5 px-1" style={{ top: zero }}>
                <span className="w-1/3 max-w-6 rounded-b" style={{ height: Math.max(2, h(r.gain)), background: GAIN(), opacity: 0.6 }} />
                <span className="w-1/3 max-w-6" />
              </span>}
            </button>
          ))}
        </div>
        {tip && (
          <div className="pointer-events-none absolute -top-2 right-0 rounded-md bg-ink px-3 py-2 text-xs text-bg shadow-soft">
            <div className="font-semibold">FY {tip.fy}</div>
            <div>Net capital gain {money(tip.gain, { cents: true })}</div>
            <div>Distributions {money(tip.income, { cents: true })}</div>
          </div>
        )}
      </div>
      <div className="flex gap-3">{rows.map((r) => <span key={r.fy} className={`flex-1 text-center text-[11px] ${r.fy === selected ? "font-semibold text-ink" : "text-ink-muted"}`}>{r.fy}</span>)}</div>
    </div>
  );
}

/** What's held, by value (or cost when there's no price yet). */
export function Allocation({ rows }: { rows: { code: string; name: string | null; value: number; priced: boolean }[] }) {
  const total = rows.reduce((t, r) => t + r.value, 0) || 1;
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  return (
    <div className="space-y-2.5">
      {sorted.map((r) => (
        <div key={r.code} title={`${r.code}: ${money(r.value, { cents: true })} (${((r.value / total) * 100).toFixed(1)}%)${r.priced ? "" : " at cost, no price yet"}`}>
          <div className="flex justify-between text-sm"><span><span className="font-semibold">{r.code}</span> <span className="text-xs text-ink-muted">{r.name}</span></span><span className="num">{money(r.value)} <span className="text-xs text-ink-muted">{((r.value / total) * 100).toFixed(0)}%</span></span></div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-surface-raised"><div className="h-full rounded-full" style={{ width: `${Math.max(1, (r.value / total) * 100)}%`, background: r.priced ? tokenColour("primary") : tokenColour("primary", 0.45) }} /></div>
        </div>
      ))}
    </div>
  );
}

/** Money invested (cost of what's held) over time, from the trades. */
export function InvestedLine({ points }: { points: { date: string; cost: number }[] }) {
  const [i, setI] = useState<number | null>(null);
  if (points.length < 2) return <p className="text-sm text-ink-muted">Add trades to see this.</p>;
  const W = 600; const H = 140; const pad = 4;
  const t0 = parseISO(points[0].date).getTime(); const t1 = parseISO(points.at(-1)!.date).getTime() || t0 + 1;
  const max = Math.max(1, ...points.map((p) => p.cost));
  const x = (d: string) => pad + ((parseISO(d).getTime() - t0) / Math.max(1, t1 - t0)) * (W - pad * 2);
  const y = (v: number) => H - pad - (v / max) * (H - pad * 2);
  // A step line: the amount stays flat until the next trade.
  let path = `M ${x(points[0].date)} ${y(points[0].cost)}`;
  for (let k = 1; k < points.length; k++) path += ` H ${x(points[k].date)} V ${y(points[k].cost)}`;
  const area = `${path} V ${H - pad} H ${x(points[0].date)} Z`;
  const p = i === null ? null : points[i];
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-36 w-full" preserveAspectRatio="none" onMouseLeave={() => setI(null)}
        onMouseMove={(e) => { const r = (e.currentTarget as SVGElement).getBoundingClientRect(); const fx = ((e.clientX - r.left) / r.width) * W;
          let best = 0; points.forEach((q, k) => { if (Math.abs(x(q.date) - fx) < Math.abs(x(points[best].date) - fx)) best = k; }); setI(best); }}>
        <path d={area} fill={tokenColour("primary", 0.12)} />
        <path d={path} fill="none" stroke={tokenColour("primary")} strokeWidth={2} vectorEffect="non-scaling-stroke" />
        {p && <line x1={x(p.date)} x2={x(p.date)} y1={0} y2={H} stroke={tokenColour("text-muted", 0.5)} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
      </svg>
      {p && <div className="pointer-events-none absolute right-0 top-0 rounded-md bg-ink px-3 py-1.5 text-xs text-bg shadow-soft">{format(parseISO(p.date), "d MMM yyyy")}: {money(p.cost)} invested</div>}
      <div className="flex justify-between text-[11px] text-ink-muted"><span>{format(parseISO(points[0].date), "MMM yyyy")}</span><span>{format(parseISO(points.at(-1)!.date), "MMM yyyy")}</span></div>
    </div>
  );
}
